import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Loader2 } from "lucide-react";

export default function FoundingSuccess() {
  const [sp] = useSearchParams();
  const { refresh } = useAuth();
  const sessionId = sp.get("session_id");
  const [state, setState] = useState("checking"); // checking | done | error
  const [info, setInfo] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/founding/status/${sessionId}`);
        if (!active) return;
        setInfo(data);
        if (data.payment_status === "paid") {
          setState("done");
          refresh?.();
          return;
        }
      } catch { /* keep polling */ }
      tries.current += 1;
      if (tries.current < 12) setTimeout(poll, 2000);
      else if (active) setState("error");
    };
    poll();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center bg-[var(--ff-bg)] px-6" data-testid="founding-success-page">
      <div className="card-flat max-w-lg w-full p-10 text-center">
        {state === "checking" && (
          <>
            <Loader2 size={40} className="animate-spin text-[var(--ff-primary)] mx-auto" />
            <h1 className="font-display text-2xl mt-5">Confirming your $1 payment…</h1>
            <p className="mt-2 text-sm text-[var(--ff-text-secondary)]">Just a moment while we lock in your founding membership.</p>
          </>
        )}
        {state === "done" && (
          <>
            <CheckCircle2 size={48} className="text-[var(--ff-primary)] mx-auto" data-testid="founding-success-check" />
            <h1 className="font-display text-3xl mt-5">Welcome to the table! 🎉</h1>
            <p className="mt-3 text-[var(--ff-text-secondary)]">
              You're officially a <strong>Founding Member</strong> of The Farm To You
              {info?.membership?.founder_number ? ` — #${info.membership.founder_number}` : ""}.
            </p>
            <p className="mt-4 text-sm text-[var(--ff-text-muted)]">
              Your $1 is credited toward your membership. The remaining
              {info ? ` $${info.due_on_launch}` : " $149"} of your $150 annual membership will be charged on
              {info ? ` ${info.billing_date_label}` : " November 27, 2026"}, then auto-renews at $150/year. Cancel anytime.
            </p>
            <Link to="/account" className="btn-primary mt-7 inline-flex justify-center" data-testid="founding-to-account">
              Go to my account
            </Link>
          </>
        )}
        {state === "error" && (
          <>
            <h1 className="font-display text-2xl">We couldn't confirm your payment yet</h1>
            <p className="mt-3 text-sm text-[var(--ff-text-secondary)]">
              If you completed checkout, your membership may still be processing. Check your account in a
              few minutes, or reach out and we'll sort it out right away.
            </p>
            <div className="mt-6 flex gap-3 justify-center">
              <Link to="/account" className="btn-secondary">My account</Link>
              <Link to="/founding" className="btn-primary">Try again</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
